import DashboardLayout from "@/components/DashboardLayout";
import { AccountingDashboard } from "@/components/installations/AccountingDashboard";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText } from "lucide-react";
import { useState } from "react";
import { FinancePageHeader } from "@/components/finance/ui";

const INCOME_TABS = [
  "Operativa de facturación",
  "Facturas emitidas",
  "Cobros",
  "Vencidos y retenciones",
  "Rectificativas",
] as const;

export default function IncomeView() {
  const [activeTab, setActiveTab] = useState<(typeof INCOME_TABS)[number]>("Operativa de facturación");

  const renderActiveWorkspace = () => {
    if (activeTab === "Operativa de facturación") {
      return (
        <AccountingDashboard
          mode="income"
          titleOverride="Operativa de facturación"
          descriptionOverride="Hitos de facturación por instalación, estado, cliente, expediente, importe y vencimiento."
        />
      );
    }

    if (activeTab === "Facturas emitidas") {
      return (
        <AccountingDashboard
          mode="income"
          initialStatusFilters={["invoiced", "sent_to_client", "pending_collection", "collected"]}
          titleOverride="Facturas emitidas"
          descriptionOverride="Hitos con factura registrada, pendientes de cobro o ya cobrados. No duplica ingresos fuera del billing canónico."
        />
      );
    }

    if (activeTab === "Cobros") {
      return (
        <AccountingDashboard
          mode="income"
          initialStatusFilters={["collected"]}
          titleOverride="Cobros"
          descriptionOverride="Hitos cobrados con fecha e importe de cobro registrados en billing."
        />
      );
    }

    if (activeTab === "Vencidos y retenciones") {
      return (
        <AccountingDashboard
          mode="income"
          initialStatusFilters={["invoiced", "sent_to_client", "pending_collection"]}
          initialOverdueFilter="yes"
          titleOverride="Vencidos y retenciones"
          descriptionOverride="Cobros pendientes con vencimiento superado. Retenciones quedan como dominio futuro, sin datos inventados."
        />
      );
    }

    return (
      <Card className="border-dashed shadow-2xs">
        <CardContent className="flex flex-col gap-3 px-5 py-10 text-sm text-muted-foreground">
          <div className="flex items-center gap-2 text-base font-semibold text-foreground">
            <FileText className="h-4 w-4 text-primary" />
            <span>Rectificativas</span>
          </div>
          <p className="max-w-xl leading-relaxed text-xs">
            En preparación. Una factura rectificativa tendrá documento, numeración y trazabilidad propios,
            vinculada a la factura original sin reescribir el hito histórico.
          </p>
        </CardContent>
      </Card>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <FinancePageHeader
          eyebrow="Finanzas y Contabilidad"
          title="Facturación e ingresos"
          description="Ingresos canónicos desde instalaciones, hitos de facturación, facturas emitidas y cobros."
        />

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as typeof activeTab)} className="space-y-4">
          <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 p-1 bg-muted/40 rounded-lg border border-border/60">
            {INCOME_TABS.map((tab) => (
              <TabsTrigger key={tab} value={tab} className="text-xs font-medium px-3 py-1.5">
                {tab}
              </TabsTrigger>
            ))}
          </TabsList>
          {INCOME_TABS.map((tab) => (
            <TabsContent key={tab} value={tab} className="mt-4 focus-visible:outline-none">
              {activeTab === tab ? renderActiveWorkspace() : null}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
